export const OrderFormValidator = () => ({
  errors: {
    recipient: [],
    shipping: [],
    invoice: [],
    buyerID: []
  },

  init() {
    // 監聽表單欄位變化，即時驗證
    this.$nextTick(() => {
      this.bindFieldListeners()
      this.cleanupRailsErrors()
      // 隱藏所有伺服器端錯誤文字
      this.hideAllServerErrorTexts()
    })
  },

  bindFieldListeners() {
    const form = this.$el.closest('form')
    if (!form) return

    // 購買人資訊欄位
    const nameField = form.querySelector('[name*="[name]"]')
    const emailField = form.querySelector('[name*="[email]"]')
    const phoneField = form.querySelector('[name*="[phone_number]"]')
    const countryCodeField = form.querySelector('[name*="[country_code]"]')
    const idTypeField = form.querySelector('[name*="[id_type]"]')
    const idNumberField = form.querySelector('[name*="[id_number]"]')

    if (nameField) {
      nameField.addEventListener('blur', () => this.validateName(nameField.value))
      // 在輸入時也驗證，以便即時移除錯誤樣式
      nameField.addEventListener('input', () => {
        if (this.errors.recipient.some(e => e.field === 'name')) {
          this.validateName(nameField.value)
        }
      })
    }
    if (emailField) {
      emailField.addEventListener('blur', () => this.validateEmail(emailField.value))
      emailField.addEventListener('input', () => {
        if (this.errors.recipient.some(e => e.field === 'email')) {
          this.validateEmail(emailField.value)
        }
      })
    }
    if (phoneField) {
      phoneField.addEventListener('blur', () => this.validatePhone(phoneField.value, countryCodeField.value))
      phoneField.addEventListener('input', () => {
        if (this.errors.recipient.some(e => e.field === 'phone_number')) {
          this.validatePhone(phoneField.value, countryCodeField.value)
        }
      })
    }

    if (idNumberField) {
      idNumberField.addEventListener('blur', () => this.validateIdNumber(idNumberField.value, idTypeField.value))
      idNumberField.addEventListener('input', () => {
        if (this.errors.recipient.some(e => e.field === 'id_number')) {
          this.validateIdNumber(idNumberField.value, idTypeField.value)
        }
      })
    }

    // 物流方式變化時驗證
    const shippingMethodInputs = form.querySelectorAll('[name*="[shipping_method]"]')
    shippingMethodInputs.forEach(input => {
      input.addEventListener('change', () => this.validateShipping())
    })

    // 發票方式變化時驗證
    const invoiceMethodInputs = form.querySelectorAll('[name="invoice_method"]')
    invoiceMethodInputs.forEach(input => {
      input.addEventListener('change', () => {
        // 清除發票相關錯誤
        this.clearSectionErrors('invoice')
        // 清除所有發票相關欄位的錯誤樣式
        this.clearAllInvoiceFieldStyles()
        // 不在這裡立即驗證，等使用者 blur 或提交表單時再驗證
      })
    })

    // 發票載具驗證
    const mobileCarrierInput = form.querySelector('[name="order[invoice_carrier_number]"]')
    if (mobileCarrierInput) {
      mobileCarrierInput.addEventListener('blur', () => {
        // 只在選擇了手機載具時才驗證
        const invoiceMethod = form.querySelector('[name="order[invoice_method]"]:checked')?.value
        if (invoiceMethod === 'mobile_carrier') {
          this.validateMobileCarrier(mobileCarrierInput.value)
        }
      })
      mobileCarrierInput.addEventListener('input', () => {
        if (this.errors.invoice.some(e => e.field === 'mobile_carrier')) {
          this.validateMobileCarrier(mobileCarrierInput.value)
        }
      })
    }

    const companyVatInput = form.querySelector('[name="order[invoice_company_vat]"]')
    if (companyVatInput) {
      companyVatInput.addEventListener('blur', () => {
        // 只在選擇了公司統編時才驗證
        const invoiceMethod = form.querySelector('[name="order[invoice_method]"]:checked')?.value
        if (invoiceMethod === 'company_vat') {
          this.validateCompanyVat(companyVatInput.value)
        }
      })
      companyVatInput.addEventListener('input', () => {
        if (this.errors.invoice.some(e => e.field === 'company_vat')) {
          this.validateCompanyVat(companyVatInput.value)
        }
      })
    }
  },

  validateName(value) {
    this.clearFieldError('name')
    const input = this.getFieldInput('name')

    if (!value || value.trim() === '') {
      this.addFieldError('recipient', 'name', '請輸入姓名')
      this.addErrorStyle(input)
      return false
    }

    if (value.length < 1 || value.length > 50) {
      this.addFieldError('recipient', 'name', '姓名長度必須在 1-50 字元之間')
      this.addErrorStyle(input)
      return false
    }

    this.removeErrorStyle(input)
    return true
  },

  validateEmail(value) {
    this.clearFieldError('email')
    const input = this.getFieldInput('email')

    if (!value || value.trim() === '') {
      this.addFieldError('recipient', 'email', '請輸入電子信箱')
      this.addErrorStyle(input)
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      this.addFieldError('recipient', 'email', '電子信箱格式不正確')
      this.addErrorStyle(input)
      return false
    }

    this.removeErrorStyle(input)
    return true
  },

  validatePhone(value, countryCode) {
    this.clearFieldError('phone_number')
    const input = this.getFieldInput('phone_number')

    if (!value || value.trim() === '') {
      this.addFieldError('recipient', 'phone_number', '請輸入聯絡電話')
      this.addErrorStyle(input)
      return false
    }

    if (countryCode === '+886') {
      // 台灣手機號碼：09 開頭 + 8 位數字
      const taiwanPhoneRegex = /^09[0-9]{8}$/
      if (!taiwanPhoneRegex.test(value)) {
        this.addFieldError('recipient', 'phone_number', '手機號碼格式不正確，請輸入09開頭的10位數字')
        this.addErrorStyle(input)
        return false
      }
    } else {
      // 中國或其他：保持原驗證邏輯
      const phoneRegex = /^\d{8,15}$/
      if (!phoneRegex.test(value)) {
        this.addFieldError('recipient', 'phone_number', '電話號碼格式不正確，請輸入8-15位數字')
        this.addErrorStyle(input)
        return false
      }
    }

    this.removeErrorStyle(input)
    return true
  },

  validateIdNumber(number, type) {
    this.clearFieldError('id_number')
    const input = this.getFieldInput('id_number')

    let maxLen = 0
    let lengthAtLeast = 0
    let allowedLengths = []

    // [["中國身份证", "001"], ["护照", "002"], ["港澳通行证", "005"], ["台湾通行证", "006"]],
    if (type === '001') {
      maxLen = 18
    } else if (type === '002') {
      lengthAtLeast = 9
    } else if (type === '005') {
      allowedLengths = [9, 11]
    } else if (type === '006') {
      maxLen = 8
    }

    // 單一長度要求
    if (maxLen > 0 && number && number.length !== maxLen) {
      this.addFieldError('shipping', 'id_number', `證號長度應為 ${maxLen} 個字元`)
      this.addErrorStyle(input)
      return false
    }

    // 最小長度要求-護照
    if (lengthAtLeast > 0 && number && number.length < lengthAtLeast) {
      this.addFieldError('shipping', 'id_number', `證號長度至少需 ${lengthAtLeast} 個字元`)
      this.addErrorStyle(input)
      return false
    }

    // 允許多個長度的情況-港澳通行證
    if (allowedLengths.length > 0 && number && !allowedLengths.includes(number.length)) {
      this.addFieldError('shipping', 'id_number', `證號長度應為 ${allowedLengths.join(' 或 ')} 個字元`)
      this.addErrorStyle(input)
      return false
    }

    this.removeErrorStyle(input)
    return true
  },

  validateShipping() {
    const form = this.getForm()
    if (!form) return false

    // 物流方式是 select 下拉選單，不是 radio button
    const shippingMethodInput = form.querySelector('[name*="[shipping_method]"]')

    // 如果找不到物流欄位（例如服務商品訂單），跳過驗證
    if (!shippingMethodInput) {
      this.clearSectionErrors('shipping')
      return true
    }

    const shippingMethod = shippingMethodInput.value

    this.clearSectionErrors('shipping')

    if (!shippingMethod) {
      this.addFieldError('shipping', 'method', '請選擇配送方式')
      return false
    }

    // 根據配送方式檢查對應欄位
    if (shippingMethod === 'home_delivery') {
      return this.validateHomeDeliveryFields(form)
    } else if (shippingMethod === 'convenience_store') {
      return this.validateConvenienceStoreFields(form)
    } else if (shippingMethod === 'overseas_delivery') {
      return this.validateOverseasDeliveryFields(form)
    }

    return true
  },

  validateHomeDeliveryFields(form) {
    let isValid = true
    const requiredFields = ['country', 'level_1', 'level_2', 'address']

    requiredFields.forEach(field => {
      const input = form.querySelector(`[name*="[${field}]"]`)
      if (!input || !input.value.trim()) {
        this.addFieldError('shipping', field, this.getFieldDisplayName(field) + '不能為空')
        isValid = false
      }
    })

    return isValid
  },
  // 海外國家可能沒有level_2欄位，所以不驗證，CN, HK, MO 除外
  validateOverseasDeliveryFields(form) {
    let isValid = true
    const requiredFields = ['country', 'level_1', 'address']

    requiredFields.forEach(field => {
      const input = form.querySelector(`[name*="[${field}]"]`)
      if (!input || !input.value.trim()) {
        this.addFieldError('shipping', field, this.getFieldDisplayName(field) + '不能為空')
        isValid = false
      }
    })

    // 針對 CN, HK, MO 驗證 level_2
    const countryInput = form.querySelector('[name*="[country]"]')
    const country = countryInput ? countryInput.value : ''
    if (['CN', 'HK', 'MO'].includes(country)) {
      const level2Input = form.querySelector('[name*="[level_2]"]')
      if (!level2Input || !level2Input.value.trim()) {
        this.addFieldError('shipping', 'level_2', this.getFieldDisplayName('level_2') + '不能為空')
        isValid = false
      }
    }

    return isValid
  },

  validateConvenienceStoreFields(form) {
    let isValid = true
    const requiredFields = ['store_id', 'store_name', 'store_address']

    requiredFields.forEach(field => {
      const input = form.querySelector(`[name*="[${field}]"]`)
      if (!input || !input.value.trim()) {
        this.addFieldError('shipping', field, '請選擇取貨門市')
        isValid = false
        return false // 只需要顯示一次錯誤
      }
    })

    return isValid
  },

  validateMobileCarrier(value) {
    this.clearFieldError('mobile_carrier')
    const input = document.querySelector('[name="order[invoice_carrier_number]"]')

    const invoiceMethod = document.querySelector('[name="order[invoice_method]"]:checked')?.value
    if (invoiceMethod !== 'mobile_carrier') {
      this.removeErrorStyle(input)
      return true
    }

    // 如果選擇了手機載具，則必須填寫
    if (!value || value.trim() === '') {
      this.addFieldError('invoice', 'mobile_carrier', '請輸入手機載具號碼')
      this.addErrorStyle(input)
      return false
    }

    // 手機載具格式：/開頭 + 7位英數字（參考 invoices 頁面的格式）
    const carrierRegex = /^\/[A-Z0-9.\-+]{7}$/
    if (!carrierRegex.test(value)) {
      this.addFieldError('invoice', 'mobile_carrier', '手機載具格式不正確，請輸入如：/ABCD123 的格式')
      this.addErrorStyle(input)
      return false
    }

    this.removeErrorStyle(input)
    return true
  },

  validateCompanyVat(value) {
    this.clearFieldError('company_vat')
    const input = document.querySelector('[name="order[invoice_company_vat]"]')

    const invoiceMethod = document.querySelector('[name="order[invoice_method]"]:checked')?.value
    if (invoiceMethod !== 'company_vat') {
      this.removeErrorStyle(input)
      return true
    }

    // 如果選擇了公司統編，則必須填寫
    if (!value || value.trim() === '') {
      this.addFieldError('invoice', 'company_vat', '請輸入統一編號')
      this.addErrorStyle(input)
      return false
    }

    // 統編格式：8位數字
    const vatRegex = /^\d{8}$/
    if (!vatRegex.test(value)) {
      this.addFieldError('invoice', 'company_vat', '統一編號格式不正確，請輸入8位數字')
      this.addErrorStyle(input)
      return false
    }

    this.removeErrorStyle(input)
    return true
  },

  validateInvoice() {
    const form = this.getForm()
    if (!form) return false

    // 不需要驗證的情況
    if (this.shouldSkipInvoiceValidation(form)) {
      this.clearSectionErrors('invoice')
      return true
    }

    // 驗證發票方式是否已選擇
    const invoiceMethod = this.getSelectedInvoiceMethod(form)
    if (!invoiceMethod) {
      this.addFieldError('invoice', 'method', '請選擇發票方式')
      return false
    }

    // 根據發票方式進行特定驗證
    return this.validateByInvoiceMethod(form, invoiceMethod)
  },

  getForm() {
    return this.$el.closest('form')
  },

  shouldSkipInvoiceValidation(form) {
    // 發票區塊不存在（品牌不開立發票）
    const invoiceSection = form.querySelector('[data-section="invoice"]')
    if (!invoiceSection) return true

    // 海外宅配不需要發票
    const shippingMethod = form.querySelector('[name*="[shipping_method]"]')?.value
    if (shippingMethod === 'overseas_delivery') return true

    return false
  },

  getSelectedInvoiceMethod(form) {
    return form.querySelector('[name="order[invoice_method]"]:checked')?.value
  },

  validateByInvoiceMethod(form, invoiceMethod) {
    const validators = {
      'mobile_carrier': () => {
        const input = form.querySelector('[name="order[invoice_carrier_number]"]')
        return this.validateMobileCarrier(input?.value || '')
      },
      'company_vat': () => {
        const input = form.querySelector('[name="order[invoice_company_vat]"]')
        return this.validateCompanyVat(input?.value || '')
      },
      // cloud_member, donation 等不需要額外驗證
      'default': () => true
    }

    const validator = validators[invoiceMethod] || validators['default']
    return validator()
  },

  validateAll() {
    const form = this.getForm()
    if (!form) return false

    this.clearAllErrors()

    let isValid = true

    // 驗證購買人資訊
    const nameField = form.querySelector('[name*="[name]"]')
    const emailField = form.querySelector('[name*="[email]"]')
    const phoneField = form.querySelector('[name*="[phone_number]"]')
    const idNumberField = form.querySelector('[name*="[id_number]"]')
    const countryCodeField = form.querySelector('[name*="[country_code]"]')
    const idTypeField = form.querySelector('[name*="[id_type]"]')

    if (nameField && !this.validateName(nameField.value)) isValid = false
    if (emailField && !this.validateEmail(emailField.value)) isValid = false
    if (phoneField && !this.validatePhone(phoneField.value, countryCodeField?.value)) isValid = false
    if (idNumberField && !this.validateIdNumber(idNumberField.value, idTypeField?.value)) isValid = false

    // 驗證物流
    if (!this.validateShipping()) isValid = false

    // 驗證發票
    if (!this.validateInvoice()) isValid = false

    return isValid
  },

  addFieldError(section, field, message) {
    if (!this.errors[section]) this.errors[section] = []

    // 避免重複錯誤
    const exists = this.errors[section].some(error => error.field === field)
    if (!exists) {
      this.errors[section].push({ field, message })
    }
  },

  clearFieldError(field) {
    Object.keys(this.errors).forEach(section => {
      this.errors[section] = this.errors[section].filter(error => error.field !== field)
    })
  },

  clearSectionErrors(section) {
    if (this.errors[section]) {
      this.errors[section] = []
    }
  },

  clearAllErrors() {
    this.errors = {
      recipient: [],
      shipping: [],
      invoice: [],
      buyerID: []
    }
  },

  getFieldDisplayName(field) {
    const displayNames = {
      country: '國家',
      level_1: '城市',
      level_2: '區域',
      address: '地址'
    }
    return displayNames[field] || field
  },

  hasErrors() {
    return Object.values(this.errors).some(section => section.length > 0)
  },

  getErrorSummary() {
    const summary = []

    if (this.errors.recipient.length > 0) {
      summary.push('購買人資訊')
    }

    if (this.errors.shipping.length > 0) {
      summary.push('物流資訊')
    }

    if (this.errors.buyerID.length > 0) {
      summary.push('購買人證件')
    }

    if (this.errors.invoice.length > 0) {
      summary.push('發票資訊')
    }

    return summary.length > 0 ? `請檢查：${summary.join('、')}` : ''
  },

  getFirstError() {
    // 返回第一個錯誤，用於簡潔的提示
    if (this.errors.recipient.length > 0) {
      return this.errors.recipient[0].message
    }

    if (this.errors.shipping.length > 0) {
      return this.errors.shipping[0].message
    }

    if (this.errors.buyerID.length > 0) {
      return this.errors.buyerID[0].message
    }

    if (this.errors.invoice.length > 0) {
      return this.errors.invoice[0].message
    }

    return ''
  },

  // 視覺回饋管理方法
  addErrorStyle(input) {
    if (!input) return

    input.classList.add('input-error')
    input.classList.remove('input-success')

    // 處理 DaisyUI 的 input class
    if (input.classList.contains('input')) {
      input.classList.add('input-error')
    }
  },

  removeErrorStyle(input) {
    if (!input) return

    input.classList.remove('input-error')

    // 如果有 Rails 的 field_with_errors wrapper，也處理它
    const wrapper = input.closest('.field_with_errors')
    if (wrapper) {
      // 將 input 移出 wrapper
      const parent = wrapper.parentNode
      parent.insertBefore(input, wrapper)
      wrapper.remove()
    }
  },

  addSuccessStyle(input) {
    if (!input) return

    input.classList.remove('input-error')
    // 可選：加入成功樣式
    // input.classList.add('input-success')
  },

  cleanupRailsErrors() {
    // 清理所有 Rails 產生的 field_with_errors
    const form = this.$el.closest('form')
    if (!form) return

    // 找出所有被 field_with_errors 包裹的 input
    const wrappedInputs = form.querySelectorAll('.field_with_errors input, .field_with_errors select, .field_with_errors textarea')

    wrappedInputs.forEach(input => {
      const wrapper = input.closest('.field_with_errors')
      if (wrapper && wrapper.parentNode) {
        // 保留 input 的 error class
        input.classList.add('input-error')
        // 將 input 移出 wrapper
        wrapper.parentNode.insertBefore(input, wrapper)
        wrapper.remove()
      }
    })
  },

  getFieldInput(fieldName) {
    const form = this.$el.closest('form')
    if (!form) return null

    // 根據欄位名稱找到對應的 input
    const fieldMap = {
      'name': '[name*="[name]"]',
      'email': '[name*="[email]"]',
      'phone_number': '[name*="[phone_number]"]',
      'id_number': '[name*="[id_number]"]',
      'country': '[name*="[country]"]',
      'level_1': '[name*="[level_1]"]',
      'level_2': '[name*="[level_2]"]',
      'address': '[name*="[address]"]',
      'mobile_carrier': '[name="mobile_carrier_value"]',
      'company_vat': '[name="company_vat_value"]'
    }

    return form.querySelector(fieldMap[fieldName])
  },

  // 這些方法已經被移除，因為我們不再顯示個別欄位的錯誤文字
  // 只保留上方的錯誤訊息區塊

  hideAllServerErrorTexts() {
    // 隱藏所有伺服器端錯誤文字
    const form = this.$el.closest('form')
    if (!form) return

    const errorTexts = form.querySelectorAll('p.text-red-500')
    errorTexts.forEach(error => {
      error.style.display = 'none'
    })
  },

  clearAllInvoiceFieldStyles() {
    // 清除所有發票相關欄位的錯誤樣式
    const mobileCarrierInput = this.getFieldInput('mobile_carrier')
    const companyVatInput = this.getFieldInput('company_vat')

    if (mobileCarrierInput) {
      this.removeErrorStyle(mobileCarrierInput)
    }

    if (companyVatInput) {
      this.removeErrorStyle(companyVatInput)
    }
  }
})